import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useMediaMatches, useBreakpoint, useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { useSentrySpanMarker, pendo } from '@frontend/tracking';
import { useRouter } from '@frontend/weave-router';
import { useOnClickOutside } from '@frontend/design-system';
import { Nav } from '../nav';
import { useNav } from '../nav/providers/nav-provider';
import { AnalyticsNavItem } from '../nav-content/analytics/analytics';
import { CallsNavItem } from '../nav-content/calls/calls';
import { ContactsNavItem } from '../nav-content/contacts/contacts';
import { FaxNavItem } from '../nav-content/fax/fax';
import { FormsNavItem } from '../nav-content/forms/forms';
import { HomeNavItem } from '../nav-content/home/home';
import { MarketingNavItem } from '../nav-content/marketing/marketing';
import { MessagesNavItem } from '../nav-content/messages/messages';
import { PaymentsNavItem } from '../nav-content/payments/payments';
import { ReviewsNavItem } from '../nav-content/reviews/reviews';
import { ScheduleNavItem } from '../nav-content/schedule/schedule';
import { GlobalNavNav } from './global-nav-nav';

export const FULL_NAV_WIDTH = 267;

interface INavContext {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  hamburgerButtonRef?: React.RefObject<HTMLButtonElement>;
}

const NavContext = createContext<INavContext>({ open: false, setOpen: () => {}, hamburgerButtonRef: undefined });

export const NavContextProvider = ({ children }: { children: ReactNode }) => {
  const open = useNav((state) => state.open);
  const setOpen = useNav((state) => state.setOpen);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);

  const ctxVal = useMemo(
    () => ({
      open,
      setOpen,
      hamburgerButtonRef,
    }),
    [open, setOpen]
  );

  return <NavContext.Provider value={ctxVal}>{children}</NavContext.Provider>;
};

export const useNavTrigger = () => {
  const context = useContext(NavContext);
  return context;
};

export const GlobalNav = () => {
  useSentrySpanMarker('GlobalNav mounted');
  const { setOpen, hamburgerButtonRef } = useNavTrigger();
  const insideRef = useRef<HTMLDivElement>(null);
  const { matches } = useMediaMatches();
  const breakpoint = useBreakpoint();
  const prevBreakpoint = useRef<typeof breakpoint>();
  useEffect(() => {
    //sends breakpoint event on first breakpoint calculation
    if (!prevBreakpoint.current) {
      pendo.track(`breakpoint-set.${breakpoint}`);
    } else if (prevBreakpoint.current !== breakpoint) {
      //sends breakpoint event whenever breakpoint changes
      pendo.track(`breakpoint-change.${breakpoint}`);
    }
    prevBreakpoint.current = breakpoint;
  }, [breakpoint]);

  const closeOnClickOutside = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useOnClickOutside({
    ref: insideRef,
    handler: closeOnClickOutside,
    active: matches.useLargeMax(),
    captureClick: false,
    ignoreConditions: [() => document.getElementById('pendo-resource-center-container') !== null],
    ...(hamburgerButtonRef && { ignoreRefs: [hamburgerButtonRef] }),
  });

  return (
    <>
      <PanelEngineAutoClose />
      <div className='hideforprint' style={{ height: '100%' }} data-testid='global-nav'>
        <Nav>
          <HomeNavItem />
          <MessagesNavItem />
          <CallsNavItem />
          <ScheduleNavItem />
          <PaymentsNavItem />
          <FormsNavItem />
          <FaxNavItem />
          <ContactsNavItem />
          <ReviewsNavItem />
          <AnalyticsNavItem />
          <MarketingNavItem />
        </Nav>
      </div>
    </>
  );
};

GlobalNav.Nav = GlobalNavNav;

const PanelEngineAutoClose = () => {
  const router = useRouter();
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const { setOpen } = useNavTrigger();

  useEffect(() => {
    const unsubscribe = router.subscribe('onLoad', () => {
      if (isXSmall) {
        setOpen(false);
      }
    });

    return unsubscribe;
  }, [router, isXSmall, setOpen]);

  return null;
};
